import React, { useState, useEffect } from 'react'
import { X, ZoomIn, ExternalLink, Grid, LayoutGrid } from 'lucide-react'

interface NFTItem {
  id: number
  image: string
  title: string
  description?: string
}

interface NFTGalleryProps {
  items?: NFTItem[]
  title?: string
  subtitle?: string
}

// Дефолтные превью для демонстрации
const availableImages = [
  '/preview/Midjourney1.png',
  '/preview/Midjourney2.png',
  '/preview/Midjourney6.png',
  '/preview/Midjourney12.png',
  '/preview/Midjourney29.png',
  '/preview/Midjourney31.png',
  '/preview/Midjourney32.png',
  '/preview/Midjourney33.png',
  '/preview/Midjourney41.png',
  '/preview/Midjourneyas.png',
  '/preview/Midjourneyt.png',
  '/preview/eyegalactic1.jpg',
  '/preview/eyegalactic2.jpg',
  '/preview/eyegolden.jpg',
  '/preview/eyeorganic.jpg',
  '/preview/eyestatue.jpg',
]

const defaultItems: NFTItem[] = availableImages.map((image, i) => ({
  id: i + 1,
  image,
  title: `Cosmic Alchemist #${i + 1}`,
  description: 'A unique piece from the Cosmic Alchemists collection'
}))

export const NFTGallery: React.FC<NFTGalleryProps> = ({
  items = defaultItems,
  title = 'The Collection',
  subtitle = 'Explore the universe of Cosmic Alchemists. Each piece is a unique 1/1 artwork, crafted with cosmic energy and alchemical mastery.'
}) => {
  const [selectedNFT, setSelectedNFT] = useState<NFTItem | null>(null)
  const [isLoaded, setIsLoaded] = useState<Set<number>>(new Set())
  const [gridSize, setGridSize] = useState<'small' | 'large'>('large')
  const [visibleItems, setVisibleItems] = useState<number[]>([])

  // Анимация появления элементов
  useEffect(() => {
    const timer = setTimeout(() => {
      items.forEach((_, idx) => {
        setTimeout(() => {
          setVisibleItems(prev => [...prev, idx])
        }, idx * 100)
      })
    }, 200)
    return () => clearTimeout(timer)
  }, [items])

  // Закрытие модалки по Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedNFT(null)
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [])

  const handleImageLoad = (id: number) => {
    setIsLoaded(prev => new Set(prev).add(id))
  }

  return (
    <section className="relative py-20 overflow-hidden">
      {/* Фоновые эффекты */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/20 to-black" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4">
        {/* Заголовок секции */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-white via-purple-200 to-red-200 bg-clip-text text-transparent">
            {title}
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            {subtitle}
          </p>
          
          {/* Контролы */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setGridSize('large')}
                className={`p-2 rounded-md transition-all ${gridSize === 'large' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setGridSize('small')}
                className={`p-2 rounded-md transition-all ${gridSize === 'small' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>
            <span className="text-white/40 text-sm">
              {items.length} pieces
            </span>
          </div>
        </div>

        {/* Галерея */}
        <div className={`grid gap-4 ${gridSize === 'large' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5'}`}>
          {items.map((item, idx) => (
            <div
              key={item.id}
              className={`group relative aspect-square cursor-pointer transition-all duration-700 ${
                visibleItems.includes(idx) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              onClick={() => setSelectedNFT(item)}
            >
              {/* Карточка */}
              <div className="absolute inset-0 rounded-xl overflow-hidden bg-gradient-to-br from-purple-900/30 to-red-900/30 border border-white/10 group-hover:border-purple-500/50 transition-all duration-500">
                {/* Изображение */}
                <div className="relative w-full h-full">
                  {!isLoaded.has(item.id) && (
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-black animate-pulse" />
                  )}
                  <img
                    src={item.image}
                    alt={item.title}
                    className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${
                      isLoaded.has(item.id) ? 'opacity-100' : 'opacity-0'
                    }`}
                    loading="lazy"
                    onLoad={() => handleImageLoad(item.id)}
                  />
                </div>

                {/* Оверлей при наведении */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />

                {/* Информация */}
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="text-white font-semibold text-sm truncate">{item.title}</h3>
                  <p className="text-white/60 text-xs mt-1">1/1 Unique</p>
                </div>

                {/* Иконка зума */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                  <div className="bg-black/60 backdrop-blur-sm rounded-full p-2">
                    <ZoomIn className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Номер */}
                <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-white/80">
                    #{item.id}
                  </span>
                </div>

                {/* Свечение по краям */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-red-500/20" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Кнопка "Показать ещё" */}
        <div className="text-center mt-12">
          <button 
            onClick={() => window.location.href = '/dapp'}
            className="group relative px-8 py-3 bg-gradient-to-r from-purple-600 to-red-600 rounded-full font-semibold text-white overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
          >
            <span className="relative z-10">View Full Collection</span>
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </div>
      </div>

      {/* Модальное окно */}
      {selectedNFT && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fadeIn"
          onClick={() => setSelectedNFT(null)}
        >
          <div
            className="relative max-w-4xl w-full max-h-[90vh] bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-white/10 animate-scaleIn"
            onClick={e => e.stopPropagation()}
          >
            {/* Кнопка закрытия */}
            <button
              onClick={() => setSelectedNFT(null)}
              className="absolute top-4 right-4 z-10 bg-black/60 backdrop-blur-sm rounded-full p-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="grid md:grid-cols-2 gap-0">
              {/* Изображение */}
              <div className="relative aspect-square md:aspect-auto">
                <img
                  src={selectedNFT.image}
                  alt={selectedNFT.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50 hidden md:block" />
              </div>

              {/* Информация */}
              <div className="p-8 flex flex-col justify-center">
                <span className="text-purple-400 text-sm font-medium mb-2">Cosmic Alchemists</span>
                <h3 className="text-3xl font-bold text-white mb-4">{selectedNFT.title}</h3>
                <p className="text-white/60 mb-6">
                  {selectedNFT.description || 'A unique piece from the Cosmic Alchemists collection. Each artwork tells its own story of cosmic transformation and alchemical mastery.'}
                </p>
                
                <div className="space-y-3 mb-8">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Edition</span>
                    <span className="text-white">1/1 Unique</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Collection</span>
                    <span className="text-white">Cosmic Alchemists</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Token ID</span>
                    <span className="text-white">#{selectedNFT.id}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button 
                    onClick={() => window.location.href = '/dapp'}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-red-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-500 hover:to-red-500 transition-all duration-300"
                  >
                    Mint Now
                  </button>
                  <button className="p-3 border border-white/20 rounded-lg text-white/80 hover:text-white hover:border-white/40 transition-colors">
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS анимации */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </section>
  )
}

export default NFTGallery
